'use client'

import { cn } from '@/lib/utils'
import Link from 'next/link'
import Image from 'next/image'
import { imgproxyEnabled, imgproxyLoader } from '@/utilities/imgproxy'
import { X } from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'
import { Mask } from '@/components/Mask'
import { Header } from '@/payload-types'
import { getLink } from '@/utilities/getLink'

interface MegaMenuProps {
  title: string
  description: string
  link: string
  items: any | []
  position?: 'left' | 'right'
  image?: string
  onClose: () => void
}

export function MegaMenu({
  title,
  description,
  link,
  items,
  position = 'left',
  image,
  onClose,
}: MegaMenuProps) {
  return (
    <AnimatePresence mode="wait" key={title}>
      {/* Overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        className="backdrop-blur-xs fixed inset-0 top-0 z-40 bg-black/50"
        onClick={() => {}}
      />

      {/* Menu Content */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -30 }}
        transition={{
          duration: 0.4,
          ease: [0.4, 0, 0.2, 1],
          staggerChildren: 0.1,
        }}
        className="fixed inset-x-0 top-0 z-40 h-screen bg-white pt-20"
      >
        <div className="absolute bottom-0 left-0">
          <Mask color="#4B4A4A" width={431} height={223} className="h-full w-full" />
        </div>
        <div className="container mx-auto h-full">
          <div className="grid h-full grid-cols-12 gap-8 py-12">
            {/* Content Section */}
            <motion.div
              className="col-span-7 pt-20"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{
                duration: 0.4,
                ease: [0.4, 0, 0.2, 1],
                delay: 0.1,
              }}
            >
              <motion.div
                className="mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.4, delay: 0.2 }}
              >
                <Link href={link} className="group/link" onClick={onClose}>
                  <h3 className="text-3xl font-semibold text-gray-900 group-hover/link:text-antonie-brown">
                    {title}
                  </h3>
                </Link>
                <p className="mt-2 text-lg text-gray-600">{description}</p>
              </motion.div>
              <div className="grid grid-cols-2 gap-8">
                {items.map((item: any, index: number) => (
                  <motion.div
                    key={item.name + '-' + index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 30 }}
                    transition={{
                      duration: 0.4,
                      delay: 0.3 + index * 0.1,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                  >
                    <Link
                      href={getLink(item.link)}
                      className="group/item block rounded-lg p-4 transition-all hover:bg-gray-50"
                      onClick={() => {}}
                    >
                      <h4 className="text-xl font-medium text-gray-900 group-hover/item:text-antonie-brown">
                        {item.link.label}
                      </h4>
                      <p className="mt-2 text-base text-gray-600">{item.descriptionLink}</p>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Image Section */}
            <motion.div
              className="relative col-span-5 h-full"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 30 }}
              transition={{
                duration: 0.4,
                ease: [0.4, 0, 0.2, 1],
                delay: 0.1,
              }}
            >
              {image ? (
                <Image
                  loader={imgproxyEnabled ? imgproxyLoader : undefined}
                  src={image}
                  alt={title}
                  fill
                  className=" object-cover"
                />
              ) : (
                <div className="bg-linear-to-br h-full w-full  from-stone-100 to-stone-200" />
              )}
            </motion.div>
          </div>
        </div>

        {/* Close Button */}
        <motion.button
          initial={{ opacity: 0, scale: 0.8, rotate: -90 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          exit={{ opacity: 0, scale: 0.8, rotate: 90 }}
          transition={{
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1],
            delay: 0.4,
          }}
          className="absolute right-6 top-6 rounded-full bg-white/80 p-2 shadow-lg transition-colors duration-200 hover:bg-white"
          onClick={onClose}
          aria-label="Menü schließen"
        >
          <X className="h-6 w-6 text-gray-600" />
        </motion.button>
      </motion.div>
    </AnimatePresence>
  )
}
